// src/pages/plan/ReferencePostModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Download, Paperclip, Loader2, Trash2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import planApi from '@api/planApi';

export default function ReferencePostModal({
  open, mode, refId, selectedCatId,
  onClose, onEdit, onSave, onUpdate, onDeleteFile, isAdmin
}) {
  const [title, setTitle]       = useState('');
  const [content, setContent]   = useState('');
  const [newFiles, setNewFiles] = useState([]);   // 새로 추가할 파일 목록
  const [saving, setSaving]     = useState(false);
  const [deletingFileSn, setDeletingFileSn] = useState(null);

  const isViewMode   = mode === 'view';
  const isEditMode   = mode === 'edit';
  const isCreateMode = mode === 'create';

  // 상세 조회 (view / edit 모드)
  const { data: postDetail, isLoading, refetch } = useQuery({
    queryKey: ['plan', 'refPost', refId],
    queryFn: () => planApi.getRefPost(refId),
    enabled: open && !!refId && (isViewMode || isEditMode),
  });

  // edit / create 모드 초기화
  useEffect(() => {
    if (isEditMode && postDetail) {
      setTitle(postDetail.title || '');
      setContent(postDetail.content || '');
      setNewFiles([]);
    }
    if (isCreateMode) {
      setTitle('');
      setContent('');
      setNewFiles([]);
    }
  }, [open, mode, postDetail, isCreateMode, isEditMode]);

  const handleAddFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    setNewFiles(prev => [...prev, ...selected]);
    e.target.value = '';
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingFile = async (fileSn) => {
    if (!window.confirm('파일을 삭제하시겠습니까?')) return;
    setDeletingFileSn(fileSn);
    try {
      await onDeleteFile({ refId, fileSn });
      await refetch();
    } catch (err) {
      alert('파일 삭제 실패: ' + (err?.response?.data?.message || err.message));
    } finally {
      setDeletingFileSn(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { alert('제목을 입력하세요.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('content', content || '');
      newFiles.forEach(f => fd.append('files', f));

      if (isCreateMode) {
        fd.append('catId', selectedCatId);
        await onSave(fd);
      } else if (isEditMode) {
        await onUpdate({ refId, formData: fd });
      }
      onClose();
    } catch (err) {
      alert('저장 실패: ' + (err?.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const existingFiles = postDetail?.files || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-bold text-slate-900">
            {isCreateMode ? '글쓰기' : isEditMode ? '게시글 수정' : '게시글 상세'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 transition-colors">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {(isViewMode || isEditMode) && isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : isViewMode && postDetail ? (
            /* 상세 보기 */
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{postDetail.title}</h3>
                <div className="mt-1 flex items-center gap-3 text-sm text-slate-400">
                  <span>{postDetail.regId}</span>
                  <span>•</span>
                  <span>{postDetail.regDtm?.substring(0, 10)}</span>
                  <span>•</span>
                  <span>조회 {postDetail.viewCnt}</span>
                </div>
              </div>
              <hr className="border-slate-200" />
              <div className="min-h-[120px] whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">
                {postDetail.content || <span className="text-slate-400">내용 없음</span>}
              </div>
              {existingFiles.length > 0 && (
                <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                  <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                    <Paperclip className="h-3.5 w-3.5" />
                    첨부파일 ({existingFiles.length}개)
                  </p>
                  {existingFiles.map(f => (
                    <div
                      key={f.fileSn}
                      className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3"
                    >
                      <span className="text-sm text-slate-700 flex-1 truncate">{f.orignlFileNm}</span>
                      <a
                        href={`/api/plan/references/files/download?atchFileId=${f.atchFileId}&fileSn=${f.fileSn}`}
                        download={f.orignlFileNm}
                        className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors shrink-0"
                      >
                        <Download className="h-3.5 w-3.5" />
                        다운로드
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* 작성 / 수정 폼 */
            <form id="post-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="제목을 입력하세요"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">내용</label>
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  rows={6}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="내용을 입력하세요"
                />
              </div>

              {/* 수정 모드 — 기존 파일 목록 */}
              {isEditMode && existingFiles.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    기존 첨부파일 ({existingFiles.length}개)
                  </label>
                  <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
                    {existingFiles.map(f => (
                      <div
                        key={f.fileSn}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                      >
                        <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span className="text-sm text-slate-700 flex-1 truncate">{f.orignlFileNm}</span>
                        <button
                          type="button"
                          onClick={() => handleDeleteExistingFile(f.fileSn)}
                          disabled={deletingFileSn === f.fileSn}
                          className="p-1 rounded hover:bg-red-50 hover:text-red-600 text-slate-400 transition-colors shrink-0"
                          title="파일 삭제"
                        >
                          {deletingFileSn === f.fileSn
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2 className="h-3.5 w-3.5" />
                          }
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 파일 추가 */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {isEditMode ? '파일 추가 (선택)' : '파일 첨부 (선택)'}
                </label>
                <input
                  type="file"
                  multiple
                  onChange={handleAddFiles}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-1 file:text-xs file:font-medium file:text-blue-700 cursor-pointer"
                />
                {newFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {newFiles.map((f, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1.5"
                      >
                        <Paperclip className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                        <span className="text-xs text-blue-700 flex-1 truncate">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveNewFile(idx)}
                          className="p-0.5 rounded hover:text-red-600 text-blue-400 transition-colors shrink-0"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          {isViewMode && isAdmin && (
            <button
              onClick={() => onEdit(postDetail?.refId)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              수정
            </button>
          )}
          {!isViewMode && (
            <button
              type="submit"
              form="post-form"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70 transition-colors"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              저장
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
